/**
 * Reservations API Service
 *
 * API functions for customer reservations operations.
 */

import api from '@/lib/axios';
import {
    CustomerReservationsRequest,
    CustomerReservationsResponse,
    CustomerReservationDetailResponse,
    CustomerCancelReservationRequest,
    CustomerReservationInvoiceResponse,
    CancelReservationApiResponse,
    CustomerExtensionRequest,
    ExtensionRequestApiResponse,
    UpdateReservationScheduleRequest,
} from '@/types/reservations';

/**
 * Get customer's reservations
 */
export const getReservations = async (
    params: CustomerReservationsRequest = {}
): Promise<CustomerReservationsResponse> => {
    const response = await api.get<CustomerReservationsResponse>('/customer/reservations', {
        params: {
            page: params.page || 1,
            pageSize: params.pageSize || 10,
            status: params.status,
            startDate: params.startDate,
            endDate: params.endDate,
        },
    });
    return response.data;
};

/**
 * Get reservation details by ID
 */
export const getReservationById = async (
    reservationId: string
): Promise<CustomerReservationDetailResponse> => {
    const response = await api.get<CustomerReservationDetailResponse>(
        `/customer/reservations/${reservationId}`
    );
    return response.data;
};

/**
 * Cancel a reservation
 * Returns cancellation details including refund information
 */
export const cancelReservation = async (
    reservationId: string,
    request: CustomerCancelReservationRequest
): Promise<CancelReservationApiResponse> => {
    const response = await api.post<CancelReservationApiResponse>(
        `/customer/reservations/${reservationId}/cancel`,
        request
    );
    return response.data;
};

/**
 * Request an extension for a reservation
 * Returns extension request details including additional cost
 */
export const requestExtension = async (
    reservationId: string,
    request: CustomerExtensionRequest
): Promise<ExtensionRequestApiResponse> => {
    const response = await api.post<ExtensionRequestApiResponse>(
        `/customer/reservations/${reservationId}/extend`,
        request
    );
    return response.data;
};

/**
 * Get reservation invoice (JSON metadata)
 */
export const getReservationInvoice = async (
    reservationId: string
): Promise<CustomerReservationInvoiceResponse> => {
    const response = await api.get<CustomerReservationInvoiceResponse>(
        `/customer/reservations/${reservationId}/invoice`
    );
    return response.data;
};

/**
 * Download reservation invoice as PDF
 * Returns a Blob for the PDF file
 */
export const downloadReservationInvoice = async (
    reservationId: string
): Promise<Blob> => {
    const response = await api.get(`/customer/reservations/${reservationId}/invoice/pdf`, {
        responseType: 'blob',
    });
    return response.data;
};

/**
 * Helper to trigger PDF download in browser
 */
export const downloadInvoiceAsPdf = async (
    reservationId: string,
    reservationNumber: string
): Promise<void> => {
    const blob = await downloadReservationInvoice(reservationId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${reservationNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Update the pickup time and/or return datetime for a reservation.
 * Only allowed for reservations that have not yet started (Pending/Confirmed).
 */
export const updateReservationSchedule = async (
    reservationId: string,
    request: UpdateReservationScheduleRequest
): Promise<CustomerReservationDetailResponse> => {
    const response = await api.patch<CustomerReservationDetailResponse>(
        `/customer/reservations/${reservationId}/schedule`,
        request
    );
    return response.data;
};
